import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Modal } from './components/Modal';

type ConfirmDialogOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
};

type PromptDialogOptions = {
  title?: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  readOnly?: boolean;
  showCancel?: boolean;
  danger?: boolean;
  validator?: (value: string) => string | null;
};

type DangerConfirmDialogOptions = {
  title?: string;
  message: string;
  expectedText?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  mismatchMessage?: string;
  trim?: boolean;
  ignoreCase?: boolean;
};

type DangerShortcutOptions = Omit<DangerConfirmDialogOptions, 'expectedText'>;
type ConfirmShortcutOptions = Omit<ConfirmDialogOptions, 'danger'>;

type DialogRequest =
  | {
      id: string;
      type: 'confirm';
      options: ConfirmDialogOptions;
      resolve: (value: boolean) => void;
    }
  | {
      id: string;
      type: 'prompt';
      options: PromptDialogOptions;
      resolve: (value: string | null) => void;
    };

type DialogApi = {
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  prompt: (options: PromptDialogOptions) => Promise<string | null>;
  confirmDangerInput: (options: DangerConfirmDialogOptions) => Promise<boolean>;
  confirmDelete: (options: DangerShortcutOptions) => Promise<boolean>;
  confirmReset: (options: DangerShortcutOptions) => Promise<boolean>;
  confirmCancelAccount: (options: DangerShortcutOptions) => Promise<boolean>;
  confirmRevoke: (options: ConfirmShortcutOptions) => Promise<boolean>;
  confirmSignOut: (options: ConfirmShortcutOptions) => Promise<boolean>;
};

const DialogContext = createContext<DialogApi | null>(null);

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState<DialogRequest | null>(null);
  const [, setQueue] = useState<DialogRequest[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const enqueue = useCallback((req: DialogRequest) => {
    setQueue((xs) => [...xs, req]);
  }, []);

  const ensureNext = useCallback(() => {
    setQueue((xs) => {
      if (active || xs.length === 0) return xs;
      const [next, ...rest] = xs;
      setActive(next);
      if (next.type === 'prompt') {
        setInputValue(next.options.defaultValue || '');
      } else {
        setInputValue('');
      }
      setInputError(null);
      return rest;
    });
  }, [active]);

  const closeActive = useCallback(() => {
    setActive(null);
    window.setTimeout(() => ensureNext(), 0);
  }, [ensureNext]);

  const confirm = useCallback(
    (options: ConfirmDialogOptions) =>
      new Promise<boolean>((resolve) => {
        enqueue({
          id: uid(),
          type: 'confirm',
          options,
          resolve
        });
        window.setTimeout(() => ensureNext(), 0);
      }),
    [enqueue, ensureNext]
  );

  const prompt = useCallback(
    (options: PromptDialogOptions) =>
      new Promise<string | null>((resolve) => {
        enqueue({
          id: uid(),
          type: 'prompt',
          options,
          resolve
        });
        window.setTimeout(() => ensureNext(), 0);
      }),
    [enqueue, ensureNext]
  );

  const confirmDangerInput = useCallback(
    async (options: DangerConfirmDialogOptions) => {
      const expected = String(options.expectedText || 'DELETE');
      const trim = options.trim !== false;
      const ignoreCase = options.ignoreCase !== false;
      const normalize = (value: string) => {
        const next = trim ? value.trim() : value;
        return ignoreCase ? next.toUpperCase() : next;
      };
      const entered = await prompt({
        title: options.title || '高风险操作确认',
        message: `${options.message}\n\n请输入 ${expected} 以继续执行。`,
        placeholder: options.placeholder || expected,
        confirmText: options.confirmText || '确认执行',
        cancelText: options.cancelText || '取消',
        danger: true,
        validator: (value) => (normalize(value) === normalize(expected) ? null : options.mismatchMessage || `输入不匹配，请输入 ${expected}`)
      });
      return entered !== null;
    },
    [prompt]
  );

  const confirmDelete = useCallback(
    (options: DangerShortcutOptions) =>
      confirmDangerInput({
        ...options,
        expectedText: 'DELETE',
        placeholder: options.placeholder || 'DELETE',
        mismatchMessage: options.mismatchMessage || '输入不匹配，请输入 DELETE'
      }),
    [confirmDangerInput]
  );

  const confirmReset = useCallback(
    (options: DangerShortcutOptions) =>
      confirmDangerInput({
        ...options,
        expectedText: 'RESET',
        placeholder: options.placeholder || 'RESET',
        mismatchMessage: options.mismatchMessage || '输入不匹配，请输入 RESET'
      }),
    [confirmDangerInput]
  );

  const confirmCancelAccount = useCallback(
    (options: DangerShortcutOptions) =>
      confirmDangerInput({
        ...options,
        expectedText: 'CANCEL',
        placeholder: options.placeholder || 'CANCEL',
        mismatchMessage: options.mismatchMessage || '输入不匹配，请输入 CANCEL'
      }),
    [confirmDangerInput]
  );

  const confirmRevoke = useCallback(
    (options: ConfirmShortcutOptions) =>
      confirm({
        ...options,
        danger: true,
        confirmText: options.confirmText || '确认撤销'
      }),
    [confirm]
  );

  const confirmSignOut = useCallback(
    (options: ConfirmShortcutOptions) =>
      confirm({
        ...options,
        danger: true,
        confirmText: options.confirmText || '确认退出'
      }),
    [confirm]
  );

  const api = useMemo<DialogApi>(
    () => ({
      confirm,
      prompt,
      confirmDangerInput,
      confirmDelete,
      confirmReset,
      confirmCancelAccount,
      confirmRevoke,
      confirmSignOut
    }),
    [confirm, prompt, confirmDangerInput, confirmDelete, confirmReset, confirmCancelAccount, confirmRevoke, confirmSignOut]
  );

  const onCancel = () => {
    if (!active) return;
    if (active.type === 'confirm') active.resolve(false);
    if (active.type === 'prompt') active.resolve(null);
    closeActive();
  };

  const onSubmit = () => {
    if (!active) return;
    if (active.type === 'confirm') {
      active.resolve(true);
      closeActive();
      return;
    }
    const value = inputValue;
    const err = active.options.validator ? active.options.validator(value) : null;
    if (err) {
      setInputError(err);
      return;
    }
    active.resolve(value);
    closeActive();
  };

  return (
    <DialogContext.Provider value={api}>
      {children}
      <Modal open={Boolean(active)} title={active?.options.title || '操作确认'} onClose={onCancel}>
        {active ? (
          <div className="std-dialog">
            <div className="std-dialog-message">{active.options.message}</div>
            {active.type === 'prompt' ? (
              <div className="std-dialog-inputWrap">
                <input
                  ref={inputRef}
                  className="std-dialog-input"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    if (inputError) setInputError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSubmit();
                  }}
                  placeholder={active.options.placeholder || ''}
                  readOnly={Boolean(active.options.readOnly)}
                  autoFocus
                />
                {inputError ? <div className="std-dialog-error">{inputError}</div> : null}
              </div>
            ) : null}
            <div className="std-dialog-actions">
              {active.type === 'confirm' || active.options.showCancel !== false ? (
                <button className="btn secondary" type="button" onClick={onCancel}>
                  {active.options.cancelText || '取消'}
                </button>
              ) : null}
              <button className={`btn ${active.options.danger ? 'danger' : ''}`} type="button" onClick={onSubmit}>
                {active.options.confirmText || '确定'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    return {
      confirm: async () => false,
      prompt: async () => null,
      confirmDangerInput: async () => false,
      confirmDelete: async () => false,
      confirmReset: async () => false,
      confirmCancelAccount: async () => false,
      confirmRevoke: async () => false,
      confirmSignOut: async () => false
    };
  }
  return ctx;
}
